import { InfoCircleOutlined } from '@ant-design/icons';
import { Col, Row, Tooltip } from 'antd';
import { APPLICATION_CONFIRM_RESULT, getMoment } from 'constants/GlobalConfig';
import { ProgressStatusTag } from '../common/ProgressStatusTag';
import { ScoringStatusTag } from '../common/ScoringStatusTag';
import { ColumnsType } from 'antd/lib/table/Table';

// antd の Tree data の表示を使うための型
type DataSource = AppStatusModal.StudentView['applications'][number];
const moment = getMoment();

export const createColumns = (
    handleClickTestInfo: (name: string, uuid: string) => void,
    handleShowHistory: (breadcrumb: AppStatusModal.Breadcrumb, uuid: string) => void,
    filterOptions: string[],
    breadcrumb: AppStatusModal.Breadcrumb
): ColumnsType<DataSource> => {
    return [
        {
            title: '教材名',
            fixed: true,
            width: 500,
            render: (_: any, record: any) => {
                const hasScore =
                    record?.children &&
                    record?.children.some((child: any) => {
                        return !isNaN(child.scoreGiven);
                    });

                return (
                    <Row gutter={[16, 16]}>
                        <Col span={18}>
                            {record?.isApp ? (
                                <button
                                    className='text-button'
                                    onClick={() => {
                                        handleClickTestInfo(record.title, record.uuid);
                                    }}>
                                    {record.title}
                                </button>
                            ) : (
                                <span>{record.title}</span>
                            )}
                        </Col>
                        {hasScore && (
                            <Col span={6}>
                                <button
                                    className='text-button'
                                    onClick={() => {
                                        handleShowHistory(breadcrumb, record.uuid);
                                    }}>
                                    履歴
                                </button>
                            </Col>
                        )}
                    </Row>
                );
            },
        },
        {
            title: (
                <>
                    <span style={{ marginRight: '0.5em' }}>
                        {APPLICATION_CONFIRM_RESULT.SCORE_GIVEN_AND_SCORE_MAXIMUM.TITLE}
                    </span>
                    <Tooltip title='複数回解答した場合、最新の得点・満点が表示されます'>
                        <InfoCircleOutlined />
                    </Tooltip>
                </>
            ),
            dataIndex: APPLICATION_CONFIRM_RESULT.SCORE_GIVEN_AND_SCORE_MAXIMUM.KEY,
            hidden: filterOptions.some((item) => item === APPLICATION_CONFIRM_RESULT.SCORE_GIVEN_AND_SCORE_MAXIMUM.KEY),
            render: (_: any, record: any) => {
                if (record?.isApp) {
                    return <></>;
                }

                return (
                    <>
                        <span style={{ marginRight: '1rem' }}>
                            {`${record?.scoreGiven ?? '-'} / ${record?.scoreMaximum ?? '-'}`}
                        </span>
                    </>
                );
            },
        },
        {
            width: 150,
            title: APPLICATION_CONFIRM_RESULT.ACTIVITY_PROGRESS.TITLE,
            dataIndex: APPLICATION_CONFIRM_RESULT.ACTIVITY_PROGRESS.KEY,
            hidden: filterOptions.some((item) => item === APPLICATION_CONFIRM_RESULT.ACTIVITY_PROGRESS.KEY),
            render: (_: any, record: any) => {
                if (record?.activityProgress) {
                    return <ProgressStatusTag status={record.activityProgress} />;
                }
                return <></>;
            },
        },
        {
            title: APPLICATION_CONFIRM_RESULT.GRADING_PROGRESS.TITLE,
            dataIndex: APPLICATION_CONFIRM_RESULT.GRADING_PROGRESS.KEY,
            hidden: filterOptions.some((item) => item === APPLICATION_CONFIRM_RESULT.GRADING_PROGRESS.KEY),
            render: (_: any, record: any) => {
                if (record?.gradingProgress) {
                    return <ScoringStatusTag status={record.gradingProgress} />;
                }
                return <></>;
            },
        },
        {
            title: APPLICATION_CONFIRM_RESULT.TIMESTAMP.TITLE,
            dataIndex: APPLICATION_CONFIRM_RESULT.TIMESTAMP.KEY,
            hidden: filterOptions.some((item) => item === APPLICATION_CONFIRM_RESULT.TIMESTAMP.KEY),
            render: (_: any, record: any) => {
                if (record?.updatedAt || record?.createdAt) {
                    return <span>{moment.unix(record.updatedAt ?? record.createdAt).format('YYYY/MM/DD HH:mm')}</span>;
                }
                if (record?.isApp) {
                    return <></>;
                }
                return <>-</>;
            },
        },
        {
            title: APPLICATION_CONFIRM_RESULT.COMMENT.TITLE,
            dataIndex: APPLICATION_CONFIRM_RESULT.COMMENT.KEY,
            hidden: filterOptions.some((item) => item === APPLICATION_CONFIRM_RESULT.COMMENT.KEY),
            render: (_: any, record: any) => {
                if (record?.comment) {
                    return (
                        <span style={{ width: '20ch', wordBreak: 'break-all', display: 'inline-block' }}>
                            {record.comment}
                        </span>
                    );
                }
                if (record?.isApp) {
                    return <></>;
                }
                return <>-</>;
            },
        },
        {
            title: APPLICATION_CONFIRM_RESULT.SCORING_USER_ID.TITLE,
            dataIndex: APPLICATION_CONFIRM_RESULT.SCORING_USER_ID.KEY,
            hidden: filterOptions.some((item) => item === APPLICATION_CONFIRM_RESULT.SCORING_USER_ID.KEY),
            render: (_: any, record: any) => {
                if (record?.scoringUserId) {
                    return (
                        <span style={{ width: '30ch', wordBreak: 'break-all', display: 'inline-block' }}>
                            {record.scoringUserId}
                        </span>
                    );
                }
                if (record?.isApp) {
                    return <></>;
                }
                return <>-</>;
            },
        },
        {
            title: APPLICATION_CONFIRM_RESULT.STARTED_AT.TITLE,
            dataIndex: APPLICATION_CONFIRM_RESULT.STARTED_AT.KEY,
            hidden: filterOptions.some((item) => item === APPLICATION_CONFIRM_RESULT.STARTED_AT.KEY),
            render: (_: any, record: any) => {
                if (record?.startedAt) {
                    return <span>{moment.unix(record.startedAt).format('YYYY/MM/DD HH:mm')}</span>;
                }
                if (record?.isApp) {
                    return <></>;
                }
                return <>-</>;
            },
        },
        {
            title: APPLICATION_CONFIRM_RESULT.SUBMITTED_AT.TITLE,
            dataIndex: APPLICATION_CONFIRM_RESULT.SUBMITTED_AT.KEY,
            hidden: filterOptions.some((item) => item === APPLICATION_CONFIRM_RESULT.SUBMITTED_AT.KEY),
            render: (_: any, record: any) => {
                if (record?.submittedAt) {
                    return <span>{moment.unix(record.submittedAt).format('YYYY/MM/DD HH:mm')}</span>;
                }
                if (record?.isApp) {
                    return <></>;
                }
                return <>-</>;
            },
        },
        {
            hidden: filterOptions.length <= 7,
        },
    ].filter((item) => !item.hidden);
};
